import { FlightIcon } from '@/assets/icons'
import { countLabel, pluralize } from '@/utils/plural'
import { PublicSectionGate } from '@components/PublicPage'
import { EmptyState } from '@components/TripOverview'
import { usePublicFlights } from '@hooks'
import { GetFlightGroupResponse } from '@orbit/api-types'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	getArrivalDisplay,
	getDepartureDisplay,
	getFlightCode,
	sortGroupsByDeparture,
} from '../Flights/flightHelpers'
import {
	FlightGroupCard,
	FlightGroupGrid,
	FlightGroupHeader,
	FlightGroupTitle,
	ManualPill,
	PassengerCount,
	PassengerRow,
	RouteAirport,
	RouteArrow,
	RouteBlock,
	RouteEndpoint,
	RouteLabel,
	RouteTime,
} from '../Flights/FlightsPage.styles'
import PublicSectionLayout from './PublicSectionLayout'
import { publicFlightDetailPath } from './route'

const countPassengers = (group: GetFlightGroupResponse): number => {
	const ids = new Set<number>()
	for (const leg of group.flights) {
		for (const p of leg.passengers) ids.add(p.userId)
	}
	return ids.size
}

/**
 * Public, read-only flights at `/shared/trips/:tripCode/flights`. Reuses the
 * member `FlightsPage.styles` cards and `flightHelpers` (route codes, wall-clock
 * vs instant display, chronological sort) verbatim, minus every edit affordance
 * (no add button, no "You're a passenger" pill). Cards click through to the
 * read-only flight detail page. Passengers appear as a count only — the detail
 * endpoint carries user ids, not the member list needed for avatars. Gating is
 * the shared `PublicSectionGate`.
 */
const PublicFlightsPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const navigate = useNavigate()
	const query = usePublicFlights(tripCode)

	return (
		<PublicSectionGate
			query={query}
			loadingLabel="Loading flights…"
			unavailableTitle="Flights Unavailable"
			unavailableMessage="These flights don't exist or haven't been shared publicly by the trip owner."
		>
			{data => {
				const groups = sortGroupsByDeparture(data.flightGroups)
				return (
					<PublicSectionLayout
						tripCode={tripCode ?? ''}
						title="Flights"
						subtitle={`${countLabel(groups.length, 'flight')}`}
					>
						{groups.length === 0 ? (
							<EmptyState
								icon={<FlightIcon sx={{ fontSize: 32 }} />}
								text="No flights have been added to this trip yet."
							/>
						) : (
							<FlightGroupGrid>
								{groups.map(group => {
										const firstLeg = group.flights[0]
										const lastLeg = group.flights[group.flights.length - 1]
										const hasManual = group.flights.some(l => l.isManuallyAdded)
										const passengerCount = countPassengers(group)

										const titleCode = firstLeg ? getFlightCode(firstLeg) : 'Flight'
										const depCode =
											firstLeg?.departureAirportCode ??
											firstLeg?.departureAirport?.iataCode ??
											'N/A'
										const arrCode =
											lastLeg?.arrivalAirportCode ??
											lastLeg?.arrivalAirport?.iataCode ??
											'N/A'
										const depStr = firstLeg ? getDepartureDisplay(firstLeg) : null
										const arrStr = lastLeg ? getArrivalDisplay(lastLeg) : null

										return (
											<FlightGroupCard
												key={group.id}
												onClick={() =>
													navigate(publicFlightDetailPath(tripCode ?? '', group.id))
												}
											>
												<FlightGroupHeader>
													<FlightGroupTitle>{titleCode}</FlightGroupTitle>
													{hasManual && <ManualPill>Manual</ManualPill>}
												</FlightGroupHeader>

												<RouteBlock>
													<RouteEndpoint $align="start">
														<RouteAirport>{depCode}</RouteAirport>
														<RouteLabel>Departs</RouteLabel>
														<RouteTime>{depStr ?? 'N/A'}</RouteTime>
													</RouteEndpoint>
													<RouteArrow>
														<FlightIcon sx={{ fontSize: 14 }} />
													</RouteArrow>
													<RouteEndpoint $align="end">
														<RouteAirport>{arrCode}</RouteAirport>
														<RouteLabel>Arrives</RouteLabel>
														<RouteTime>{arrStr ?? 'N/A'}</RouteTime>
													</RouteEndpoint>
												</RouteBlock>

												<PassengerRow>
													<PassengerCount>
														{passengerCount}{' '}
														{pluralize(passengerCount, 'passenger')}
													</PassengerCount>
												</PassengerRow>
											</FlightGroupCard>
										)
									})}
								</FlightGroupGrid>
						)}
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicFlightsPage
